import {
  Box,
  CloseButton,
  Icon,
  ListItem,
  OrderedList,
  Text
} from '@chakra-ui/react'
import WarningIcon from '@material-ui/icons/Warning'
import WarningSharp from '@material-ui/icons/WarningSharp'
import React from 'react'

interface TooltipContentProps {
  message: string
}

interface DivMatch {
  content: string
  index: number
}

interface AnchorMatch {
  href: string
  text: string
}

/**
 * This is a tooltip for the telehealth toggle on the app.
 */
export const TelehealthToggleTooltipContent: React.FC<TooltipContentProps> = ({
  message
}) => {
  // Helper function to parse text and convert HTML-like tags to JSX
  const parseMessage = (text: string) => {
    // First, handle ol tags by replacing them with a placeholder
    let processedText = text
    const olMatches: DivMatch[] = []
    const olIndex = 0

    // Find all ol tags and replace them with placeholders
    processedText = processedText.replace(
      /<ol[^>]*>(.*?)<\/ol>/g,
      (match, content) => {
        olMatches.push({ content, index: olIndex })
        return `__OL_${olIndex}__`
      }
    )

    // Find all div tags and replace them with placeholders
    const divMatches: DivMatch[] = []
    const divIndex = 0

    processedText = processedText.replace(
      /<div[^>]*>(.*?)<\/div>/g,
      (match, content) => {
        divMatches.push({ content, index: divIndex })
        return `__DIV_${divIndex}__`
      }
    )

    const anchorMatches: AnchorMatch[] = []
    processedText = processedText.replace(
      /<a[^>]*href="([^"]+)"[^>]*>(.*?)<\/a>/g,
      (_match, href, text) => {
        anchorMatches.push({ href, text })
        return `__A_${anchorMatches.length - 1}__`
      }
    )

    // Split by <b> tags and process each part
    const parts = processedText.split(
      /(<b>.*?<\/b>|<br\/>|__OL_\d+__|__DIV_\d+__|__A_\d+__)/
    )

    return parts.map((part, index) => {
      const key = `${index}-${part.substring(0, 20)}`

      if (part.startsWith('<b>') && part.endsWith('</b>')) {
        // Extract text between <b> tags
        const boldText = part.replace(/<\/?b>/g, '')
        return (
          <Text as="span" fontWeight="bold" key={key}>
            {boldText}
          </Text>
        )
      }

      if (part === '<br/>') {
        return <br key={key} />
      }

      // Check for ol placeholders
      const olPlaceholderMatch = part.match(/__OL_(\d+)__/)
      if (olPlaceholderMatch) {
        const olIndex = parseInt(olPlaceholderMatch[1])
        const olContent = olMatches[olIndex]?.content || ''

        // Parse li tags within the ol content
        const liItems = olContent.match(/<li[^>]*>(.*?)<\/li>/g) || []
        const parsedLiItems = liItems.map((li, liIndex) => {
          const liContent = li.replace(/<\/?li[^>]*>/g, '')
          return (
            <ListItem
              key={`li-${olIndex}-${liIndex}-${liContent.substring(0, 20)}`}
            >
              {parseMessage(liContent)}
            </ListItem>
          )
        })

        return (
          <OrderedList key={key} ml="2em" styleType="inside" spacing={2}>
            {parsedLiItems}
          </OrderedList>
        )
      }

      // Check for div placeholders
      const divPlaceholderMatch = part.match(/__DIV_(\d+)__/)
      if (divPlaceholderMatch) {
        const divIndex = parseInt(divPlaceholderMatch[1])
        const divContent = divMatches[divIndex]?.content || ''

        // Check if this is an empty div with margin-bottom styling
        if (divContent.trim() === '') {
          return <Box key={key} mb="8px" />
        }

        // If div has content, render it with styling
        return (
          <Box key={key} ml="20px">
            {parseMessage(divContent)}
          </Box>
        )
      }

      const anchorPlaceholderMatch = part.match(/__A_(\d+)__/)
      if (anchorPlaceholderMatch) {
        const aIndex = parseInt(anchorPlaceholderMatch[1])
        const a = anchorMatches[aIndex]
        return (
          <Text
            as="a"
            href={a?.href}
            target="_blank"
            rel="noopener noreferrer"
            color="blue.500"
            key={key}
          >
            {a?.text}
          </Text>
        )
      }

      // Regular text
      return (
        <Text as="span" key={key}>
          {part}
        </Text>
      )
    })
  }

  return <Box sx={{ wordBreak: 'break-word' }}>{parseMessage(message)}</Box>
}

export const WarningTooltip = ({
  message,
  onClose
}: {
  message: React.ReactNode
  onClose: () => void
}) => {
  const handleClose = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    onClose()
  }

  return (
    <Box py={2} pl={2} pr={1} display="flex" width="100%">
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flex="1"
      >
        <Icon as={WarningIcon} color="red.500" mr="16px" boxSize={6} />
        {message}
      </Box>
      <CloseButton
        onClick={handleClose}
        size="sm"
        ml={1}
        color="#757575"
        position="relative"
      />
    </Box>
  )
}

/**
 * This is a tooltip for the chrome extension UX.
 */
export const WarningTooltipExtension = ({
  message,
  onClose
}: {
  message: React.ReactNode
  onClose: () => void
}) => {
  const handleClose = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    onClose()
  }

  return (
    <Box display="flex" alignItems="flex-start">
      <Box mr={2} flex="0">
        <WarningSharp />
      </Box>
      {message}
      <CloseButton
        onClick={handleClose}
        size="sm"
        color="#757575"
        position="relative"
      />
    </Box>
  )
}
